<template>
  <section class="quest-status">
    <header class="widget-header">
      <h2>Quest Status</h2>
      <p v-if="!hasHero" class="kicker">
        Activate a hero on the
        <router-link to="/game/hero">Hero</router-link> tab to take contracts.
      </p>
      <p v-else class="kicker">Expeditions and open contracts</p>
    </header>

    <div class="bucket-list">
      <section
        v-for="bucket in buckets"
        :key="bucket.id"
        class="quest-bucket"
      >
        <div class="bucket-head">
          <h3>
            {{ bucket.label }}
            <span class="count">{{ bucket.items.length }}</span>
          </h3>
          <router-link class="view-all" :to="bucket.to">View all</router-link>
        </div>
        <p v-if="bucket.items.length === 0" class="empty-copy">
          {{ bucket.empty }}
        </p>
        <ul v-else class="quest-rows">
          <li v-for="item in bucket.items" :key="item.key">
            <router-link class="quest-row" :to="bucket.to">
              <span class="quest-title">{{ item.title }}</span>
              <span v-if="item.meta" class="quest-meta" :class="item.tone">
                {{ item.meta }}
              </span>
            </router-link>
          </li>
        </ul>
      </section>
    </div>
  </section>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref } from "vue";
import { useGameStore } from "../store/game";

const gameStore = useGameStore();
const now = ref(new Date());
let timerId = null;

const hasHero = computed(() => Boolean(gameStore.activeHero));

function isActiveQuest(quest) {
  return quest?.status === "in_progress" && quest?.end_time;
}

function isReady(quest) {
  if (!isActiveQuest(quest)) return false;
  return now.value >= new Date(quest.end_time);
}

function timeRemaining(quest) {
  const endTime = new Date(quest.end_time);
  const diff = endTime - now.value;
  if (diff <= 0) return "Ready";
  const minutes = Math.floor(diff / 60000);
  const seconds = Math.floor((diff % 60000) / 1000);
  return `${minutes}m ${seconds}s`;
}

const questTab = (tab) => ({ path: "/game/quest", query: { tab } });

const buckets = computed(() => {
  const active = Array.isArray(gameStore.activeQuests)
    ? gameStore.activeQuests
    : [];
  const available = Array.isArray(gameStore.availableQuests)
    ? gameStore.availableQuests
    : [];
  const ready = active.filter(isReady);
  const inProgress = active.filter((quest) => isActiveQuest(quest) && !isReady(quest));

  return [
    {
      id: "ready",
      label: "Ready to Claim",
      empty: "No rewards ready to claim.",
      to: questTab("active"),
      items: ready.map((quest) => ({
        key: `ready-${quest.id}`,
        title: quest.title || quest.quest_slug || quest.slug,
        meta: "READY",
        tone: "ready",
      })),
    },
    {
      id: "progress",
      label: "In Progress",
      empty: "No expeditions underway.",
      to: questTab("active"),
      items: inProgress.map((quest) => ({
        key: `progress-${quest.id}`,
        title: quest.title || quest.quest_slug || quest.slug,
        meta: timeRemaining(quest),
        tone: "",
      })),
    },
    {
      id: "available",
      label: "Available",
      empty: "No contracts available.",
      to: questTab("available"),
      items: available.map((quest) => ({
        key: `available-${quest.slug}`,
        title: quest.title || quest.slug,
        meta: quest.recommended_level
          ? `Lvl ${quest.recommended_level}`
          : "",
        tone: "",
      })),
    },
  ];
});

onMounted(() => {
  timerId = setInterval(() => {
    now.value = new Date();
  }, 1000);
});

onUnmounted(() => {
  if (timerId) clearInterval(timerId);
});
</script>

<style scoped>
.quest-status {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  padding: 0.9rem 1rem 1rem;
  box-sizing: border-box;
  color: #e7ecf1;
}

.widget-header h2 {
  margin: 0;
  font-family: "Cinzel", serif;
  font-size: 0.78rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #c9b48a;
}

.kicker {
  margin: 0.15rem 0 0;
  font-size: 0.72rem;
  color: #88c0d0;
}

.kicker a {
  color: #00f0ff;
  text-decoration: none;
}

.kicker a:hover {
  text-decoration: underline;
}

.bucket-list {
  margin-top: 0.7rem;
  overflow: auto;
  min-height: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.7rem;
}

.bucket-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 0.35rem;
}

.bucket-head h3 {
  margin: 0;
  min-width: 0;
  font-family: "Cinzel", serif;
  font-size: 0.7rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #c9b48a;
}

.count {
  margin-left: 0.35rem;
  color: #00f0ff;
  font-family: inherit;
  letter-spacing: 0;
}

.view-all {
  flex-shrink: 0;
  font-size: 0.68rem;
  color: #00f0ff;
  text-decoration: none;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.view-all:hover {
  text-decoration: underline;
}

.empty-copy {
  margin: 0;
  color: #8892b0;
  font-size: 0.78rem;
  font-style: italic;
}

.quest-rows {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}

.quest-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  min-width: 0;
  box-sizing: border-box;
  padding: 0.35rem 0.5rem;
  background: rgba(0, 240, 255, 0.08);
  border: 1px solid rgba(0, 240, 255, 0.22);
  border-radius: 4px;
  color: #d8dee9;
  text-decoration: none;
  font-size: 0.8rem;
}

.quest-row:hover {
  background: rgba(0, 240, 255, 0.16);
  border-color: #00f0ff;
}

.quest-title {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.quest-meta {
  flex-shrink: 0;
  font-size: 0.68rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #88c0d0;
}

.quest-meta.ready {
  color: #a3be8c;
  font-weight: 700;
}
</style>
